/* eslint-disable @nrwl/nx/enforce-module-boundaries */

import { QuestionCircleOutlined } from '@ant-design/icons';
import { useLazyQuery } from '@apollo/client';
import {
  Challenge,
  ChallengeApplication,
  Criteria,
  CriteriaResult,
  GET_TOP_CHALLENGE_APPLICATIONS,
} from '@frontend/data-access';
import { calculateAverageScore, CompanyAvatar } from '@frontend/shared/ui';
import { Table, Tooltip } from 'antd';
import { ColumnType } from 'antd/lib/table';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useMemo } from 'react';

interface GetTopApplications {
  application: ChallengeApplication;
  evaluations: CriteriaResult[];
}
/* eslint-disable-next-line */
export interface ChallengeDashboardApplicationEvaluationsProps {
  challenge: Challenge & { __typename?: string };
}

export function ChallengeDashboardApplicationEvaluations({
  challenge,
}: ChallengeDashboardApplicationEvaluationsProps) {
  const { t } = useTranslation('common');

  const [getTopIdeas, { data, loading }] = useLazyQuery<{
    getTopApplications: GetTopApplications[];
  }>(GET_TOP_CHALLENGE_APPLICATIONS, {
    fetchPolicy: 'no-cache',
    variables: { challengeId: challenge.id },
  });

  useEffect(() => {
    getTopIdeas({ variables: { limit: 10 } });
  }, [getTopIdeas]);

  const sortApplications = (applications?: GetTopApplications[]) => {
    return applications?.sort((a, b) => {
      const aScore =
        calculateAverageScore(
          filterChallengeExpertResults(a.application.criteriaResults)
        ) || 0;
      const bScore =
        calculateAverageScore(
          filterChallengeExpertResults(b.application.criteriaResults)
        ) || 0;
      return Number(bScore) - Number(aScore);
    });
  };

  const filterChallengeExpertResults = (results: CriteriaResult[]) => {
    return results.filter(
      (r) => r?.criteria?.type === 'CHALLENGE_EXPERT' && r?.status === 'DONE'
    );
  };

  const getFilteredResults = (sortedData?: GetTopApplications[]) => {
    return (
      sortedData?.map((application) =>
        filterChallengeExpertResults(application.application.criteriaResults)
      ) || []
    );
  };

  const getFilteredCriteria = (sortedData?: GetTopApplications[]) => {
    return (
      sortedData?.map((application) =>
        application.application.criteriaResults.filter(
          (r) => r?.criteria?.type === 'CHALLENGE_EXPERT'
        )
      ) || []
    );
  };

  const createCriteriaColumn = (
    dataIndex: string,
    shortTitle: string,
    tooltipTitle: string,
    criteria: CriteriaResult[][],
    results: CriteriaResult[][]
  ): ColumnType<GetTopApplications> => {
    return {
      dataIndex,
      key: dataIndex,
      title: (
        <span className="flex items-center gap-1">
          {shortTitle}{' '}
          <Tooltip title={tooltipTitle}>
            <QuestionCircleOutlined />
          </Tooltip>
        </span>
      ),
      render: (_, __, index: number) => {
        const value = criteria[index]?.find(
          (r) =>
            r?.criteria?.name.trim().toLowerCase() === dataIndex.toLowerCase()
        );
        if (!value) return '-';
        return (
          <CriteriaProgress data={value?.criteria} result={results[index]} />
        );
      },
    };
  };

  const sortedData = useMemo(
    () => sortApplications(data?.getTopApplications),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data]
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const results = useMemo(() => getFilteredResults(sortedData), [sortedData]);
  const criteria = useMemo(() => getFilteredCriteria(sortedData), [sortedData]);

  const columns: ColumnType<GetTopApplications>[] = [
    {
      dataIndex: 'index',
      key: 'index',
      title: t('no'),
      render: (_, __, index) => <div>{index + 1}.</div>,
    },
    {
      dataIndex: ['application', 'title'],
      key: 'title',
      title: t('top_10_solution_name'),
      render: (value) => (
        <span>
          {value.length > 30 ? value.substring(0, 30) + '...' : value}
        </span>
      ),
    },
    {
      dataIndex: 'company',
      key: 'company',
      title: t('company'),
      render: (_, record) => (
        <div className="flex space-x-2 items-center">
          <CompanyAvatar
            company={record.application.company}
            showBadge={
              record.application.company.companyPortfolio?.isScvPortfolio
            }
          />
          <div>{record.application.company.name}</div>
        </div>
      ),
    },
    {
      dataIndex: 'averageScore',
      key: 'averageScore',
      title: (
        <div className="align-middle">
          {t('avg_score')}{' '}
          <Tooltip title={t('all_criteria_average_evaluation_score')}>
            <QuestionCircleOutlined className="cursor-pointer" />
          </Tooltip>
        </div>
      ),
      render: (_, __, index: number) => {
        return <div>{calculateAverageScore(results[index]) || '-'}</div>;
      },
    },
    createCriteriaColumn(
      'desirability',
      'D',
      t('desirability_tooltip'),
      criteria,
      results
    ),
    createCriteriaColumn(
      'viability',
      'V',
      t('viability_tooltip'),
      criteria,
      results
    ),
    createCriteriaColumn(
      'feasibility',
      'F',
      t('feasibility_tooltip'),
      criteria,
      results
    ),
  ];

  return (
    <div style={{ overflowX: 'auto' }}>
      <Table
        columns={columns}
        dataSource={sortedData || []}
        pagination={false}
        bordered
        loading={loading}
        rowKey="id"
      />
    </div>
  );
}

const CriteriaProgress = ({
  data,
  result,
}: {
  data?: Criteria;
  result?: CriteriaResult[];
}) => {
  const finalResult = result?.filter(
    (r) => r?.criteria?.id === data?.id && r?.status === 'DONE'
  );
  const percent =
    !finalResult || !data?.scale
      ? 0
      : (finalResult.reduce((prev, result) => prev + result?.scale, 0) *
          data?.scale) /
        finalResult.length;

  return (
    <div className="flex justify-end text-center space-y-2 font-medium">
      {percent ? (percent / 10).toFixed(1) : '-'}
    </div>
  );
};

export default ChallengeDashboardApplicationEvaluations;
